<template>
  <div class="pc-editor">
    <AppMapPointPanelHeader :header="header" @close="$emit('close')" />
    <div class="pc-editor__content">
      <b-form>
        <slot name="floor"></slot>
        <b-form-group label="Name*" :state="$v.pcTemp.name.required">
          <b-input type="text" placeholder="Name" v-model="$v.pcTemp.name.$model" />
          <template #invalid-feedback v-if="$v.pcTemp.name.$dirty && !$v.pcTemp.name.required">PC name required</template>
        </b-form-group>
        <b-form-group label="Model">
          <b-input type="text" placeholder="pc model" v-model="$v.pcTemp.model.$model" />
        </b-form-group>
        <b-form-group label="Asset number">
          <b-input type="text" v-model="$v.pcTemp.asset_num.$model" />
        </b-form-group>
        <b-form-group label="Note">
          <b-input type="text" v-model="$v.pcTemp.description.$model" />
        </b-form-group>
        <b-form-group label="Department" :state="!pcTemp.department_id && $v.departmentTemp.name.requiredIf">
          <b-input-group v-if="pcTemp.department_id">
            <b-input v-model="$v.departmentTemp.name.$model" />
            <b-btn
              slot="append"
              @click="
                pcTemp.department_id = null
                departmentsByTerm = []
              "
              ><i class="ion ion-md-close"></i
            ></b-btn>
          </b-input-group>
          <app-select
            v-else
            @search="departmentSearch"
            @select="departmentSelect"
            ref="departmentSelect"
            :proposeNew="can(PN.DEPARTMENT_CREATE)"
            :items="departmentsByTerm"
            placeholder="select department"
          >
            <template #default="props">
              <span class="select-item">
                <span class="select-item-text">{{ props.item.name }}</span>
                <b-btn size="sm" class="align-self-center" @click.stop.prevent="departmentDestroy_(props.item.id)">
                  <i class="ion ion-md-trash"></i
                ></b-btn>
              </span>
            </template>
          </app-select>
          <template #invalid-feedback v-if="pcTemp.department_id && !$v.departmentTemp.name.requiredIf">
            Department name required
          </template>
        </b-form-group>

        <div class="btn-panel">
          <b-btn type="button" v-if="can(PN.PC_DELETE) && !isNew" variant="default" @click="destroy" title="Delete" class="mr-auto">
            <i class="ion ion-md-trash"></i
          ></b-btn>
          <div class="ml-auto"></div>
          <b-btn type="button" variant="primary" @click="save">Save</b-btn>
          <b-btn type="button" variant="default" @click="cancel">Cancel</b-btn>
        </div>
      </b-form>
    </div>
  </div>
</template>

<script>
import AppSelect from '@/components/shared/AppSelect'
import { PC_CREATE, PC_UPDATE } from '@/store/modules/pc'
import Id, { isLocalId } from '@/utils/Id'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import * as PN from '@/utils/PermissionNames'
import { required, requiredIf } from 'vuelidate/lib/validators'
import AppMapPointPanelHeader from '@/components/shared/AppMapPointPanelHeader.vue'
import { getItemHeader } from '@/components/shared/helpers'
import escape from 'lodash/escape'

export default {
  name: 'PcEditor',
  components: {
    AppMapPointPanelHeader,
    AppSelect
  },
  props: {
    id: {
      type: [Number, String],
      required: true
    },
    point: {
      type: Object,
      required: true
    }
  },
  validations() {
    return {
      pcTemp: {
        name: {
          required
        },
        model: {},
        asset_num: {},
        description: {},
        install_url: {},
        department_id: {}
      },
      departmentTemp: {
        name: {
          requiredIf: requiredIf(() => this.pcTemp.department_id)
        }
      }
    }
  },
  data: () => ({
    PN,
    pcTemp: {
      name: '',
      model: '',
      asset_num: '',
      description: '',
      install_url: '',
      department_id: '',
      avatar_updated_at: '',
      avatar_id: ''
    },
    departmentTemp: {
      name: ''
    },
    departmentsByTerm: [],
    allowedExtensions: 'gif,jpg,jpeg,png,webp'
  }),
  computed: {
    ...mapGetters('pc', ['getPc']),
    ...mapGetters('department', ['departments', 'getDepartment']),
    ...mapGetters('rbac', ['can']),
    header() {
      return getItemHeader(this.point, 'PC')
    },
    avatarUrl() {
      let suffix = this.pcTemp.avatar_id ? `/${this.pcTemp.avatar_id}` : ''
      return `/api/v1/avatars${suffix}`
    },
    department() {
      return this.getDepartment(this.pcTemp.department_id) || {}
    },
    isNew() {
      return isLocalId(this.pcTemp.id)
    }
  },
  methods: {
    ...mapActions('pc', ['pcSave', 'pcLoad']),
    ...mapActions('position', ['positionSave', 'positionDestroy']),
    ...mapActions('department', ['departmentSave', 'departmentDestroy']),
    ...mapActions('point', ['pointSave']),
    ...mapMutations('pc', {
      pcCreate_: PC_CREATE,
      pcUpdate_: PC_UPDATE
    }),
    departmentSelect(p) {
      if (typeof p === 'object') {
        this.pcTemp.department_id = p.id
      } else if (typeof p === 'string') {
        // is a new
        this.departmentSave({ id: Id(), name: p }).then((pId) => {
          this.pcTemp.department_id = pId
        })
      } else {
        throw new Error("Can't resolve returned company type")
      }
    },
    departmentSearch(term) {
      this.departmentsByTerm = []
      this.$nextTick(() => {
        this.departmentsByTerm = term
          ? this.departments.filter((department) => department.name && department.name.indexOf(term) !== -1)
          : this.departments
      })
    },
    departmentDestroy_(id) {
      const department = this.getDepartment(id)
      this.$appToast.question(`A you sure want to delete '${escape(department.name)}' department?`).then(() =>
        this.departmentDestroy(department).then(() => {
          this.$refs.departmentSelect.search('')
        }))
    },
    save() {
      this.$v.$touch()
      if (!this.$v.$invalid) {
        const save = () => {
          const pointData = { id: this.point.id, title: this.pcTemp.name }
          const pointPromise = this.pointSave(pointData)

          pointPromise.then((pointId) => {
            this.pcTemp.point_id = pointId
            const payload = { pc: this.pcTemp, config: { params: { expand: 'point' } } }
            this.pcSave(payload).then(() => {
              this.$emit('save', { pointId })
            })
          })
        }

        if (this.pcTemp.department_id) {
          this.departmentSave(this.departmentTemp).then((departmentId) => {
            this.pcTemp.department_id = departmentId
            save()
          })
        } else {
          save()
        }
      }
    },
    cancel() {
      this.$emit('cancel')
    },
    destroy() {
      this.$appToast.question('A you sure want to delete pc?').then(() => {
        this.$emit('destroy')
      })
    }
  },
  watch: {
    id: {
      immediate: true,
      handler(id) {
        this.pcTemp = Object.assign({ id }, this.pcTemp, this.getPc(id))
      }
    },
    department: {
      immediate: true,
      handler(value) {
        this.departmentTemp = Object.assign({}, this.departmentTemp, value)
      }
    }
  }
}
</script>

<style lang="scss">
.pc-editor__content {
  padding: 24px;
}
.select-item {
  display: flex;
  justify-items: left;

  .btn {
    margin-left: auto;
  }
}
</style>
